import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, App, Badge, Button, Drawer, Empty, Input, List, Popconfirm, Space, Spin, Tag, Typography, theme } from 'antd';
import { DeleteOutlined, DownloadOutlined, HistoryOutlined, SaveOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  createShareLink,
  deleteProductCalculation,
  fetchProductCalculations,
  loadProductCalculation,
  logRequestFailure,
  saveProductCalculation,
  saveResult,
  updateProductCalculation,
  type ProductCalculationDetail,
  type ProductCalculationListItem,
} from '../../shared/api/client';
import {
  classifyOwnershipFailure,
  decideSaveAction,
  deriveListState,
  isSaveTitleValid,
  type LoadedRecord,
  type SaveAction,
} from './productHistoryLogic';
import { HISTORY_DRAWER_Z_INDEX, stickyToolbarStyle } from '../../shared/lib/stickyToolbar';
import { useVisibleBox } from '../../shared/lib/visibleBox';

interface ProductHistoryProps {
  /** К нему привязана история — `ProductEntry.productId` из `App.tsx`. */
  productId: string;
  /**
   * Собирает текущий payload калькулятора в момент нажатия «Сохранить».
   * Функция, а не готовый объект: DashboardView не может подписать виджет на
   * каждое изменение калькулятора ради значения, нужного только по клику.
   */
  buildPayload: () => Record<string, unknown>;
  /** Выбранная запись подставляется в калькулятор — решает DashboardView. */
  onLoad: (detail: ProductCalculationDetail) => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const ProductHistory: React.FC<ProductHistoryProps> = ({ productId, buildPayload, onLoad }) => {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const { token } = theme.useToken();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<ProductCalculationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Запись, из которой сейчас загружен расчёт — решает `decideSaveAction`, надо
  // ли следующее сохранение считать перезаписью. Сбрасывается при расхождении
  // названия (см. `handleTitleChange`), чтобы «сохранить под старым именем после
  // правки нового» снова создавало отдельную запись, а не тихо переименовывало
  // исходную.
  const [loadedRecord, setLoadedRecord] = useState<LoadedRecord | null>(null);

  // Товар, к которому относится ТЕКУЩЕЕ состояние панели. С ним сверяется каждый
  // уже улетевший запрос: пока ответ по товару A едет, слот платформы может
  // переключиться на товар B, и запись ответа A под карточку B — это чужие авторы
  // в списке и id, которых в B нет.
  const productIdRef = useRef(productId);
  // Номер последнего отправленного запроса списка. Отменить сам запрос нечем:
  // `fetchProductCalculations` сигнала не принимает (в отличие от `fetchKeepaData`
  // в том же `shared/api/client.ts`), поэтому право записать состояние остаётся
  // только у последнего отправленного, а обогнавший его ответ отбрасывается —
  // иначе два refresh подряд (после сохранения и после удаления) могли лечь в
  // обратном порядке.
  const listRequestSeqRef = useRef(0);

  const refresh = useCallback(() => {
    // Вызов из устаревшего замыкания (например, `performSave` по товару A, когда
    // панель уже про B) не идёт в сеть и не зажигает спиннер: его ответ всё равно
    // был бы отброшен, а `loading` остался бы висеть навсегда.
    if (productId !== productIdRef.current) return;
    const seq = listRequestSeqRef.current + 1;
    listRequestSeqRef.current = seq;
    const isCurrent = () => seq === listRequestSeqRef.current && productId === productIdRef.current;
    setLoading(true);
    setHasError(false);
    fetchProductCalculations(productId)
      .then((list) => {
        if (isCurrent()) setItems(list);
      })
      .catch(() => {
        if (!isCurrent()) return;
        setHasError(true);
        message.error(t('idea_calc.product_history.load_error'));
      })
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Смена товара начинает панель заново. Сброс здесь, а не `key={productId}` на
  // стороне DashboardView: контракт «сбрось меня снаружи» из самого виджета не
  // виден, молча теряется в любом новом месте монтирования и проверяется только
  // тестом целого дашборда — а этот сброс лежит рядом с состоянием, которое
  // стережёт, и покрыт тестом самого виджета.
  // ⚠️ Любое новое состояние, привязанное к ОДНОМУ товару, обязано сбрасываться
  // здесь. Цена пропуска: оставшийся `loadedRecord` заставляет `decideSaveAction`
  // вернуть перезапись, PUT уходит на запись чужого товара, бэкенд отвечает 404 —
  // работа не сохранена.
  useEffect(() => {
    productIdRef.current = productId;
    setTitle('');
    setLoadedRecord(null);
    setItems([]);
    setLoadingId(null);
    setDeletingId(null);
    refresh();
  }, [productId, refresh]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    // Правка названия отвязывает поле от загруженной записи — иначе «загрузил,
    // переименовал, сохранил» перезаписало бы исходный расчёт под новым именем.
    if (loadedRecord && value.trim() !== loadedRecord.title.trim()) {
      setLoadedRecord(null);
    }
  };

  const performSave = async (action: SaveAction, trimmedTitle: string) => {
    // Товар фиксируется на момент клика: запрос уходит именно в ту карточку,
    // которую пользователь видел, а состояние панели после ответа правится, только
    // если она всё ещё про неё же.
    const requestedProductId = productId;
    setSaving(true);
    try {
      if (action.kind === 'overwrite') {
        await updateProductCalculation(requestedProductId, action.id, trimmedTitle, buildPayload());
      } else {
        await saveProductCalculation(requestedProductId, trimmedTitle, buildPayload());
        // Создание новой записи отвязывает поле — иначе повторный клик «Сохранить»
        // без правок молча создал бы ещё один дубликат вместо перезаписи.
        if (requestedProductId === productIdRef.current) {
          setTitle('');
          setLoadedRecord(null);
        }
      }
      message.success(t('idea_calc.product_history.save_success'));
      refresh();
    } catch (error) {
      const status = error instanceof ApiError ? error.status : undefined;
      const reason = action.kind === 'overwrite' ? classifyOwnershipFailure(status) : 'generic';
      const savingMessages = {
        forbidden: 'idea_calc.product_history.update_forbidden',
        // Перезаписывать нечего: запись удалили. Повтор «Сохранить» под общим
        // текстом ошибки дал бы ровно тот же отказ — пользователю нужен другой ход.
        missing: 'idea_calc.product_history.update_missing',
        generic: 'idea_calc.product_history.save_error',
      } as const;
      message.error(t(savingMessages[reason]));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!isSaveTitleValid(title)) {
      message.warning(t('idea_calc.product_history.title_required'));
      return;
    }
    const trimmedTitle = title.trim();
    const action = decideSaveAction(loadedRecord, trimmedTitle);

    if (action.kind === 'overwrite') {
      modal.confirm({
        title: t('idea_calc.product_history.overwrite_confirm_title'),
        content: t('idea_calc.product_history.overwrite_confirm_content'),
        okText: t('idea_calc.common.save'),
        cancelText: t('idea_calc.common.cancel'),
        // embed живёт в модалке платформы — без явного zIndex подтверждение
        // рисуется под её хромом (тот же приём, что в Calculator.tsx).
        zIndex: 1200,
        onOk: () => performSave(action, trimmedTitle),
        // onCancel не задан: отмена ничего не делает — ни новой записи, ни перезаписи.
      });
      return;
    }

    void performSave(action, trimmedTitle);
  };

  /**
   * Поделиться = тот же путь, что у «Поделиться» в `UserHistoryBar`: расчёт
   * сохраняется в историю пользователя (`saved_results`) под введённым
   * названием и получает share-id. Так расчёт из карточки виден в разделе
   * «Приложения» и открывается по ссылке — `product_calculations` своих ссылок
   * не имеет (права там по товару, а не по владельцу, см. модель).
   *
   * Ссылка — публичная `/s/<id>` модуля из ответа бэкенда, а не ссылка
   * платформы: карточку встраивает не только платформа, но и Launch Pipeline,
   * и origin его страницы дал бы адрес, по которому модуля нет. Публичную
   * ссылку Launch Pipeline умеет привязать к лаунчу.
   */
  const handleShare = async () => {
    if (!isSaveTitleValid(title)) {
      message.warning(t('idea_calc.product_history.title_required'));
      return;
    }
    const trimmedTitle = title.trim();
    setSharing(true);
    try {
      const saved = await saveResult(trimmedTitle, buildPayload());
      if (!saved.success) {
        message.error(saved.message || t('idea_calc.user_history.save_error'));
        return;
      }
      const { url: link } = await createShareLink(trimmedTitle);
      try {
        await navigator.clipboard.writeText(link);
        message.success(t('idea_calc.user_history.share_copied'));
      } catch {
        // Буфер недоступен (нет разрешения, небезопасный контекст) — показываем
        // ссылку, чтобы пользователь скопировал её руками.
        modal.info({ title: t('idea_calc.user_history.share_link_label'), content: link, zIndex: 1200 });
      }
    } catch (error: unknown) {
      logRequestFailure('Product history share error:', error);
      message.error(t('idea_calc.user_history.share_error'));
    } finally {
      setSharing(false);
    }
  };

  const handleLoad = async (id: number) => {
    const requestedProductId = productId;
    setLoadingId(id);
    try {
      const detail = await loadProductCalculation(requestedProductId, id);
      // Пока запись ехала, слот мог переключиться на другой товар: подставить её
      // тогда значило бы положить расчёт товара A в карточку B и привязать поле
      // названия к записи, которой в B нет — следующее сохранение ушло бы PUT'ом
      // на чужую запись.
      if (requestedProductId !== productIdRef.current) return;
      setTitle(detail.title);
      setLoadedRecord({ id: detail.id, title: detail.title });
      onLoad(detail);
    } catch {
      message.error(t('idea_calc.product_history.load_calc_error'));
    } finally {
      // Флаг спиннера принадлежит текущему товару: гасить его ответом по прошлому
      // значило бы снять спиннер с чужой строки.
      if (requestedProductId === productIdRef.current) setLoadingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    const requestedProductId = productId;
    setDeletingId(id);
    try {
      await deleteProductCalculation(requestedProductId, id);
      message.success(t('idea_calc.product_history.delete_success'));
      refresh();
    } catch (error) {
      const status = error instanceof ApiError ? error.status : undefined;
      const reason = classifyOwnershipFailure(status);
      if (reason === 'missing') {
        // Записи уже нет — цель действия достигнута, но список её ещё показывает.
        message.info(t('idea_calc.product_history.delete_missing'));
        refresh();
        return;
      }
      message.error(
        reason === 'forbidden'
          ? t('idea_calc.product_history.delete_forbidden')
          : t('idea_calc.product_history.delete_error'),
      );
    } finally {
      if (requestedProductId === productIdRef.current) setDeletingId(null);
    }
  };

  // Сайдбар встаёт по видимой части блока модуля: иначе его шапка прибита к
  // верху всего расчёта и уезжает за экран при первой же прокрутке.
  const visibleBox = useVisibleBox(anchorRef, open);

  const listState = deriveListState(loading, hasError, items.length);

  return (
    <>
      {/* Строка липнет к верху СВОЕГО блока, а не к окну: `fixed` в embed прилип бы
          к вьюпорту страницы платформы и наехал бы на её хром. `sticky` держит её
          видимой при прокрутке расчёта и при этом не выходит за наш слот.
          Поле названия и «Сохранить» живут здесь, а не в сайдбаре истории: сайдбар
          абсолютно позиционирован и на узкой ширине перекрывал их вторым слоем UI
          поверх точно этих же элементов. Слой и фон — в `stickyToolbarStyle`, там
          же объяснено, почему z-index обязан быть выше табличного. */}
      <div ref={anchorRef} style={stickyToolbarStyle(token.colorBgLayout, token.paddingXS)}>
        <Space.Compact style={{ flex: '0 1 340px', minWidth: 200 }}>
          <Input
            placeholder={t('idea_calc.product_history.title_placeholder')}
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            onPressEnter={handleSave}
            disabled={saving || sharing}
          />
          <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={sharing} onClick={handleSave}>
            {t('idea_calc.product_history.save_button')}
          </Button>
        </Space.Compact>

        <Button icon={<ShareAltOutlined />} loading={sharing} disabled={saving} onClick={handleShare}>
          {t('idea_calc.user_history.share_button')}
        </Button>

        <Badge count={items.length} size="small" offset={[-4, 4]}>
          <Button
            icon={<HistoryOutlined />}
            // Повторное нажатие закрывает: кнопка выглядит как переключатель
            // панели, и «открыть ещё раз» — единственное, чего от неё не ждут.
            onClick={() => setOpen((isOpen) => !isOpen)}
          >
            {t('idea_calc.product_history.open_button')}
          </Button>
        </Badge>
      </div>

      <Drawer
        title={t('idea_calc.product_history.title')}
        placement="right"
        open={open}
        onClose={() => setOpen(false)}
        width={420}
        // Без маски намеренно. Маска antd перекрывает ВСЮ страницу и перехватывает
        // клики — в embed это заблокировало бы хром платформы, пока панель открыта
        // (воспроизведено в харнессе: клик по кнопке страницы не проходил). История —
        // вспомогательный сайдбар, а не модальный режим: хост обязан остаться живым.
        mask={false}
        // getContainer={false} — рендер НА МЕСТЕ, без портала в document.body.
        // С порталом шторка позиционируется от окна и растягивается во всю его высоту
        // поверх интерфейса платформы (видно на живом стенде 2026-08-21): модуль живёт
        // в модалке хоста, и вылезать за её границы он не вправе. Абсолютное
        // позиционирование считается от обёртки контента в DashboardView, которой для
        // этого задан position: relative.
        getContainer={false}
        rootStyle={{ position: 'absolute', top: visibleBox?.top ?? 0, height: visibleBox?.height }}
        // Слой задаётся явно: дефолт приходит из темы ХОСТА (antd — синглтон),
        // и с ним шапка сайдбара уходила под липкую строку действий.
        zIndex={HISTORY_DRAWER_Z_INDEX}
        destroyOnClose={false}
      >
      {listState === 'loading' && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}

      {listState === 'error' && (
        <Alert type="error" showIcon message={t('idea_calc.product_history.load_error')} />
      )}

      {listState === 'empty' && <Empty description={t('idea_calc.product_history.empty_state')} />}

      {listState === 'ready' && (
        <List
          size="small"
          dataSource={items}
          renderItem={(item) => (
            <List.Item
              key={item.id}
              actions={[
                <Button
                  key="load"
                  size="small"
                  type="primary"
                  ghost
                  icon={<DownloadOutlined />}
                  loading={loadingId === item.id}
                  onClick={() => handleLoad(item.id)}
                >
                  {t('idea_calc.product_history.load_button')}
                </Button>,
                <Popconfirm
                  key="delete"
                  title={t('idea_calc.product_history.delete_confirm_title')}
                  onConfirm={() => handleDelete(item.id)}
                  okText={t('idea_calc.common.yes')}
                  cancelText={t('idea_calc.common.no')}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={deletingId === item.id}
                  >
                    {t('idea_calc.common.delete')}
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={
                  <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    {item.title}
                    {item.source === 'mcp' && (
                      <Tag color="purple" title={t('idea_calc.common.source_mcp_tooltip')}>
                        {t('idea_calc.common.source_mcp')}
                      </Tag>
                    )}
                  </span>
                }
                description={
                  <>
                    <Typography.Text type="secondary" style={{ display: 'block' }}>
                      {t('idea_calc.product_history.saved_by', {
                        owner: item.owner_name,
                        date: formatDate(item.created_at),
                      })}
                    </Typography.Text>
                    {/* Строка появляется ТОЛЬКО у перезаписанной записи: у остальных
                        `updated_at` приходит null, и показывать «изменено: никогда»
                        значило бы засорять список ради пустоты. */}
                    {item.updated_at && (
                      <Typography.Text type="secondary" style={{ display: 'block' }}>
                        {t('idea_calc.product_history.changed_by', {
                          owner: item.updated_by_name ?? item.owner_name,
                          date: formatDate(item.updated_at),
                        })}
                      </Typography.Text>
                    )}
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
      </Drawer>
    </>
  );
};

export default ProductHistory;
